import type { ButtonHTMLAttributes } from "react";
import { cx } from "../../lib/utils";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger";
}

// Reference mockups don't name a distinct "secondary"/"danger" button
// anywhere — only a filled primary (bg-primary) and a plain/outlined
// surface button (bg-surface-container-low, used for "Dismiss"/"Cancel"/
// "Copy Text" etc). Mapped: secondary/ghost -> that surface treatment,
// danger -> the same surface treatment plus hover:text-error (the "Dismiss"
// button's own convention in the conversation-detail mockup).
const VARIANTS: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary: "bg-primary hover:bg-secondary text-on-primary shadow-sm",
  secondary: "bg-surface-container-low hover:bg-surface-container text-on-surface shadow-sm",
  ghost: "text-on-surface-variant hover:bg-surface-container hover:text-on-surface",
  danger: "bg-surface-container-lowest hover:bg-surface-container text-on-surface-variant hover:text-error border border-outline-variant/40 shadow-sm",
};

export function Button({ variant = "primary", className, ...rest }: ButtonProps) {
  return (
    <button
      className={cx(
        "inline-flex items-center justify-center gap-spacing-xs px-spacing-md py-spacing-xs rounded-xl font-label-md text-label-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
        VARIANTS[variant],
        className
      )}
      {...rest}
    />
  );
}
